import { Grid, TextField, Typography, Checkbox } from '@material-ui/core'
import { createFilterOptions } from '@material-ui/lab/Autocomplete'
import { useCallback, useEffect, useState } from 'react'

import { SelecionarData, Autocomplete, WrapperFiltros } from 'components'
import { getLabelStatusCadeiaDeCustodia } from 'helpers/labels'
import { useFormState } from 'hooks/useFormState'
import { useSetorService } from 'hooks/useSetorService'
import { useUsuarioService } from 'hooks/useUsuarioService'
import { useVestigioService } from 'hooks/useVestigioService'

import useStyles from './styles'

const STATUS_CADEIA_CUSTODIA = ['COM_USUARIO', 'GUARDADO', 'ENCAMINHADO', 'DESCARTADO', 'EM_PASSAGEM_CUSTODIA', 'DESMEMBRADO']

const VALORES_INICIAIS_FILTRO = {
  search: '',
  setoresOrigem: [],
  setores: [],
  tipos: [],
  tiposEspecificos: [],
  statusCadeiaCustodia: [],
  usuariosCadastro: [],
  dataInicio: new Date(),
  dataFinal: new Date(),
  dataEntregaInicio: new Date(),
  dataEntregaFinal: new Date(),
  descricao: '',
  lacreAtual: '',
  localizacao: '',
  lote: '',
  dataChecked: false,
  dataEntregaChecked: false,
}

export const FiltrosBancoVestigios = ({ setVestigios, setProcessandoVestigios }) => {
  const classes = useStyles()
  const vestigioService = useVestigioService()
  const setorService = useSetorService()
  const usuarioService = useUsuarioService()
  const filterOptions = createFilterOptions({
    trim: true,
    limit: 50,
  })
  const formFiltros = useFormState({}, VALORES_INICIAIS_FILTRO)
  const [options, setOptions] = useState({
    setores: [],
    usuarios: [],
    tiposVestigios: [],
    tiposVestigiosEspecificos: [],
  })
  const [loadingOptions, setLoadingOptions] = useState(true)
  const [optionsError, setOptionsError] = useState(false)

  useEffect(() => {
    let mounted = true

    const carregarOptions = async () => {
      try {
        const [setores, usuarios, tiposVestigios, tiposVestigiosEspecificos] = await Promise.all([
          setorService.listarSetoresAsync(),
          usuarioService.listarUsuariosAsync(),
          vestigioService.listarTiposVestigiosAsync(),
          vestigioService.listarTiposVestigiosEspecificosAsync(),
        ])

        if (!mounted) return

        if (!setores || !usuarios || !tiposVestigios || !tiposVestigiosEspecificos) {
          setOptionsError(true)
        }

        setOptions({
          setores: setores || [],
          usuarios: usuarios || [],
          tiposVestigios: tiposVestigios || [],
          tiposVestigiosEspecificos: tiposVestigiosEspecificos || [],
        })
      } catch (error) {
        if (mounted) setOptionsError(true)
      } finally {
        if (mounted) setLoadingOptions(false)
      }
    }

    carregarOptions()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!formFiltros.formState.values.dataFinal) {
      const dataAtual = new Date()
      dataAtual.setHours(0, 0, 0, 0)
      const dataDiaAnterior = new Date(dataAtual.getTime())
      dataDiaAnterior.setDate(dataAtual.getDate() - 1)

      formFiltros.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          dataInicio: dataDiaAnterior,
          dataFinal: dataAtual,
        },
      }))
    }
    if (!formFiltros.formState.values.dataEntregaFinal) {
      const dataAtual = new Date()
      dataAtual.setHours(0, 0, 0, 0)
      const dataDiaAnterior = new Date(dataAtual.getTime())
      dataDiaAnterior.setDate(dataAtual.getDate() - 1)

      formFiltros.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          dataEntregaInicio: dataDiaAnterior,
          dataEntregaFinal: dataAtual,
        },
      }))
    }
  }, [])

  const filtrarVestigios = () => {
    const dataInicioFormatada = new Date(formFiltros.formState.values.dataInicio)
    const dataEntregaInicioFormatada = new Date(formFiltros.formState.values.dataEntregaInicio)
    dataInicioFormatada.setHours(0, 0, 0, 0)
    dataEntregaInicioFormatada.setHours(0, 0, 0, 0)

    vestigioService.filtrarVestigios({ ...formFiltros.formState.values, dataInicio: dataInicioFormatada, dataEntregaInicio: dataEntregaInicioFormatada }, setVestigios, setProcessandoVestigios)
  }

  const handleLimparFiltros = useCallback(async () => {
    formFiltros.setFormState((prevState) => ({
      ...prevState,
      values: { ...prevState.values, ...VALORES_INICIAIS_FILTRO },
    }))

    setVestigios([])
    vestigioService.filtrarVestigios(VALORES_INICIAIS_FILTRO, setVestigios, setProcessandoVestigios)
  }, [formFiltros])

  const getSetorLabel = (option) => `${option.nome.toUpperCase()} / ${option.divisao.sigla.toUpperCase()} / ${option.divisao.regional.nome.toUpperCase()} / ${option.divisao.regional.orgao.sigla.toUpperCase()}`

  const getUsuarioLabel = (option) => `${option.nome.toUpperCase()}${option.cargo ? ` - ${option.cargo.nome.toUpperCase()}` : ''}`

  return (
    <WrapperFiltros handleLimparFiltros={handleLimparFiltros} handleFiltrar={filtrarVestigios} error={optionsError} >
      {/* Seção 1: Dados do Vestígio */}
      <Grid item md={4} sm={12} xs={12} className={classes.grupoFiltros}>
        <Typography variant="subtitle2" className={classes.grupoTitle}>
          Dados do Vestígio
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete 
              value={formFiltros.formState.values.tipos}
              multiple
              name="tipos"
              options={options.tiposVestigios}
              loading={loadingOptions}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'tipos')}
              label="TIPO GENÉRICO DO VESTÍGIO"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              value={formFiltros.formState.values.tiposEspecificos}
              multiple
              name="tiposEspecificos"
              options={options.tiposVestigiosEspecificos}
              loading={loadingOptions}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'tiposEspecificos')}
              label="TIPO ESPECÍFICO DO VESTÍGIO"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" label="NÚMERO DO LACRE ATUAL" name="lacreAtual" onChange={formFiltros.handleChange} value={formFiltros.formState.values.lacreAtual} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" label="NÚMERO DO LOTE" name="lote" onChange={formFiltros.handleChange} value={formFiltros.formState.values.lote} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" label="DESCRIÇÃO" name="descricao" onChange={formFiltros.handleChange} value={formFiltros.formState.values.descricao} multiline minRows={2} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </Grid>

      {/* Seção 2: Localização e Custódia */}
      <Grid item md={4} sm={12} xs={12} className={classes.grupoFiltros}>
        <Typography variant="subtitle2" className={classes.grupoTitle}>
          Localização e Custódia
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              value={formFiltros.formState.values.setores}
              multiple
              name="setores"
              filterOptions={filterOptions}
              options={options.setores}
              loading={loadingOptions}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'setores')}
              getOptionLabel={getSetorLabel}
              getTagLabel={(option) => option.sigla}
              getTagTooltip={getSetorLabel}
              label="SETOR ATUAL"
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={formFiltros.formState.values.setoresOrigem}
              multiple
              name="setoresOrigem"
              options={options.setores}
              loading={loadingOptions}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'setoresOrigem')}
              getOptionLabel={getSetorLabel}
              getTagLabel={(option) => option.sigla}
              getTagTooltip={getSetorLabel}
              label="SETOR DE RECEBIMENTO"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" label="LOCAL GUARDADO ATUAL" name="localizacao" onChange={formFiltros.handleChange} value={formFiltros.formState.values.localizacao} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={formFiltros.formState.values.statusCadeiaCustodia}
              multiple
              name="statusCadeiaCustodia"
              options={STATUS_CADEIA_CUSTODIA}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'statusCadeiaCustodia')}
              getOptionLabel={getLabelStatusCadeiaDeCustodia}
              label="STATUS"
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={formFiltros.formState.values.usuariosCadastro}
              multiple
              name="usuariosCadastro"
              filterOptions={filterOptions}
              options={options.usuarios}
              loading={loadingOptions}
              onChange={(event, newValue) => formFiltros.handleChangeNewValueByNameProperty(event, newValue, 'usuariosCadastro')}
              getOptionLabel={getUsuarioLabel}
              getTagLabel={(option) => option.nome}
              getTagTooltip={getUsuarioLabel}
              label="USUÁRIO RESPONSÁVEL PELO CADASTRO"
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Seção 3: Período de Cadastro e de Entrega */}
      <Grid item md={4} sm={12} xs={12} className={classes.grupoFiltros}>
        <Typography variant="subtitle2" className={classes.grupoTitle}>
          Período de Cadastro
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item md={1} sm={2} xs={2} style={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox checked={formFiltros.formState.values.dataChecked || false} onChange={formFiltros.handleChange} inputProps={{ 'aria-label': 'controlled' }} name="dataChecked" />
          </Grid>
          <Grid item md={5} sm={5} xs={10}>
            <SelecionarData
              dataBD={formFiltros.formState.values.dataInicio}
              withoutTime
              callback={(valor) => formFiltros.handleChangeCampoData('dataInicio', valor)}
              label="Data do cadastro - Início"
              disabled={!formFiltros.formState.values.dataChecked}
              name="dataInicio"
              maxDate={formFiltros.formState.values.dataFinal}
            />
          </Grid>
          <Grid item md={6} sm={5} xs={12}>
            <SelecionarData dataBD={formFiltros.formState.values.dataFinal} withoutTime callback={(valor) => formFiltros.handleChangeCampoData('dataFinal', valor)} label="Data do cadastro - Final" disabled={!formFiltros.formState.values.dataChecked} name="dataFinal" />
          </Grid>
        </Grid>
        <Typography variant="subtitle2" className={classes.grupoTitle} style={{ marginTop: 16 }}>
          Período de Entrega
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item md={1} sm={2} xs={2} style={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox checked={formFiltros.formState.values.dataEntregaChecked || false} onChange={formFiltros.handleChange} inputProps={{ 'aria-label': 'controlled' }} name="dataEntregaChecked" />
          </Grid>
          <Grid item md={5} sm={5} xs={10}>
            <SelecionarData
              dataBD={formFiltros.formState.values.dataEntregaInicio}
              withoutTime
              callback={(valor) => formFiltros.handleChangeCampoData('dataEntregaInicio', valor)}
              label="Data de entrega - Início"
              disabled={!formFiltros.formState.values.dataEntregaChecked}
              name="dataEntregaInicio"
              maxDate={formFiltros.formState.values.dataFinal}
            />
          </Grid>
          <Grid item md={6} sm={5} xs={12}>
            <SelecionarData dataBD={formFiltros.formState.values.dataEntregaFinal} withoutTime callback={(valor) => formFiltros.handleChangeCampoData('dataEntregaFinal', valor)} label="Data de entrega - Final" disabled={!formFiltros.formState.values.dataEntregaChecked} name="dataEntregaFinal" />
          </Grid>
        </Grid>
      </Grid>
    </WrapperFiltros>
  )
}
